// Closed-trade discipline: win rate, payoff, expectancy, profit factor,
// plus Kelly fraction and a risk-of-ruin gauge.

import { useQuery } from '@tanstack/react-query';
import { Dices, Skull, Target, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtMoney, fmtPercent } from '@/lib/format';
import { cn } from '@/lib/cn';

export function RiskOfRuinCard() {
  const q = useQuery({ queryKey: ['trade-stats'], queryFn: api.tradeStats });
  const d = q.data;

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Dices className="size-4 text-indigo-300" />
          <h3 className="text-sm font-medium text-zinc-200">Edge &amp; ruin</h3>
        </div>
        <span className="text-[11px] text-zinc-400">
          {d ? `${d.closed} closed trades` : '—'}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat
          icon={Target}
          label="Win rate"
          value={d ? fmtPercent(d.winRate * 100) : '—'}
        />
        <Stat
          icon={TrendingUp}
          label="Payoff"
          value={d ? `${d.payoffRatio.toFixed(2)}×` : '—'}
          hint="avg win ÷ |avg loss|"
        />
        <Stat
          label="Expectancy"
          value={d ? fmtMoney(d.expectancy) : '—'}
          hint="per trade"
          tone={(d?.expectancy ?? 0) >= 0 ? 'pos' : 'neg'}
        />
        <Stat
          label="Profit factor"
          value={d ? d.profitFactor.toFixed(2) : '—'}
          hint="wins ÷ losses"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <KellyGauge fraction={d?.kellyFraction ?? 0} />
        <RuinGauge pct={d?.riskOfRuinPct ?? 0} />
      </div>

      <p className="text-[10px] text-zinc-400 leading-snug">
        Kelly fraction is the *theoretical max* per-trade bet given your edge —
        in practice most disciplined traders use a quarter-Kelly or less.
        Risk-of-ruin estimates the chance of losing 20R of capital at flat 1-R
        bets given current win rate × payoff.
      </p>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon?: typeof Target;
  label: string;
  value: string;
  hint?: string;
  tone?: 'pos' | 'neg';
}) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/30 px-2.5 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-400">
        {Icon && <Icon className="size-3" />}
        {label}
      </div>
      <div
        className={cn(
          'text-sm font-medium num mt-0.5',
          tone === 'pos' ? 'text-emerald-400' : tone === 'neg' ? 'text-rose-400' : 'text-zinc-100',
        )}
      >
        {value}
      </div>
      {hint && <div className="text-[9px] text-zinc-400">{hint}</div>}
    </div>
  );
}

function KellyGauge({ fraction }: { fraction: number }) {
  const pct = fraction * 100;
  return (
    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-400">Kelly</div>
      <div className="text-lg num font-medium text-emerald-300">{pct.toFixed(1)}%</div>
      <div className="mt-1 h-1.5 bg-zinc-900 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <div className="text-[9px] text-zinc-400 mt-0.5">¼-Kelly: {(pct / 4).toFixed(1)}%</div>
    </div>
  );
}

function RuinGauge({ pct }: { pct: number }) {
  const colour =
    pct < 5
      ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/[0.06]'
      : pct < 25
        ? 'text-amber-300 border-amber-500/30 bg-amber-500/[0.06]'
        : 'text-rose-300 border-rose-500/30 bg-rose-500/[0.06]';
  return (
    <div className={cn('rounded-md border px-3 py-2', colour)}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5">
        <Skull className="size-3" /> Risk of ruin
      </div>
      <div className="text-lg num font-medium">{pct.toFixed(1)}%</div>
      <div className="mt-1 h-1.5 bg-zinc-900 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full',
            pct < 5 ? 'bg-emerald-500' : pct < 25 ? 'bg-amber-500' : 'bg-rose-500',
          )}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <div className="text-[9px] text-zinc-400 mt-0.5">over 20 R-units</div>
    </div>
  );
}
